import { useState, useCallback, useMemo } from "react";
import { isNewSeries } from "../utils/showFields";
import { getDisplayPlatforms } from "../utils/networkNames";

const useAdvancedFilters = (releases = []) => {
    // Search state
    const [searchQuery, setSearchQuery] = useState("");
    
    // Filter states
    const [selectedGenres, setSelectedGenres] = useState([]);
    const [selectedNetworks, setSelectedNetworks] = useState([]);
    const [selectedContentRatings, setSelectedContentRatings] = useState([]);
    // Series type state - "all", "new", or "returning"
    const [seriesType, setSeriesType] = useState("all");

    // Extract unique genres and networks from releases
    const availableGenres = useMemo(() => {
        const genreSet = new Set();
        releases.forEach(release => {
            if (release.genres && Array.isArray(release.genres)) {
                release.genres.forEach(genre => genreSet.add(genre));
            }
        });
        return Array.from(genreSet).sort();
    }, [releases]);

    const availableNetworks = useMemo(() => {
        const networkSet = new Set();
        releases.forEach(release => {
            // Include both networks array and sourceName
            if (release.networks && Array.isArray(release.networks)) {
                release.networks.forEach(network => networkSet.add(network));
            }
            if (release.sourceName) {
                networkSet.add(release.sourceName);
            }
        });
        return Array.from(networkSet).sort();
    }, [releases]);

    const availableContentRatings = useMemo(() => {
        const ratingSet = new Set();
        releases.forEach(release => {
            if (release.contentRating) {
                ratingSet.add(release.contentRating);
            }
        });
        return Array.from(ratingSet).sort();
    }, [releases]);

    // Multi-field search function
    const matchesSearch = useCallback((release, query) => {
        if (!query) return true;
        
        const searchTerm = query.toLowerCase();
        
        // Search in title
        if (release.title && release.title.toLowerCase().includes(searchTerm)) {
            return true;
        }
        
        // Search in plot overview
        if (release.plotOverview && release.plotOverview.toLowerCase().includes(searchTerm)) {
            return true;
        }
        
        // Search in source name
        if (release.sourceName && release.sourceName.toLowerCase().includes(searchTerm)) {
            return true;
        }
        
        // Search in the platform name the card shows
        if (getDisplayPlatforms(release).toLowerCase().includes(searchTerm)) {
            return true;
        }

        // Search in genres
        if (release.genres && Array.isArray(release.genres)) {
            if (release.genres.some(genre => genre.toLowerCase().includes(searchTerm))) {
                return true;
            }
        }
        
        // Search in networks
        if (release.networks && Array.isArray(release.networks)) {
            if (release.networks.some(network => network.toLowerCase().includes(searchTerm))) {
                return true;
            }
        }
        
        return false;
    }, []);

    // Genre filter function
    const matchesGenres = useCallback((release, genres) => {
        if (genres.length === 0) return true;
        
        if (!release.genres || !Array.isArray(release.genres)) return false;
        
        return genres.some(selectedGenre => 
            release.genres.some(releaseGenre => 
                releaseGenre.toLowerCase() === selectedGenre.toLowerCase()
            )
        );
    }, []);

    // Network filter function
    const matchesNetworks = useCallback((release, networks) => {
        if (networks.length === 0) return true;
        
        // Check both networks array and sourceName
        const releaseNetworks = [
            ...(release.networks || []),
            ...(release.sourceName ? [release.sourceName] : [])
        ];
        
        return networks.some(selectedNetwork => 
            releaseNetworks.some(releaseNetwork => 
                releaseNetwork.toLowerCase() === selectedNetwork.toLowerCase()
            )
        );
    }, []);

    const matchesContentRatings = useCallback((release, ratings) => {
        if (ratings.length === 0) return true;
        if (!release.contentRating) return false;

        return ratings.some(selectedRating =>
            release.contentRating.toLowerCase() === selectedRating.toLowerCase()
        );
    }, []);

    // Apply all filters
    const filteredReleases = useMemo(() => {
        return releases.filter(release => {
            // Search filter
            if (!matchesSearch(release, searchQuery)) return false;
            
            // Genre filter
            if (!matchesGenres(release, selectedGenres)) return false;
            
            // Network filter
            if (!matchesNetworks(release, selectedNetworks)) return false;

            // Content rating filter
            if (!matchesContentRatings(release, selectedContentRatings)) return false;
            
            // Series type filter
            if (seriesType === "new" && !isNewSeries(release)) return false;
            if (seriesType === "returning" && isNewSeries(release)) return false;
            
            return true;
        });
    }, [
        releases, 
        searchQuery, 
        selectedGenres, 
        selectedNetworks, 
        selectedContentRatings,
        seriesType,
        matchesSearch,
        matchesGenres,
        matchesNetworks,
        matchesContentRatings
    ]);

    // Reset all filters
    const resetFilters = useCallback(() => {
        setSearchQuery("");
        setSelectedGenres([]);
        setSelectedNetworks([]);
        setSelectedContentRatings([]);
        setSeriesType("all");
    }, []);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return searchQuery !== "" || 
               selectedGenres.length > 0 || 
               selectedNetworks.length > 0 || 
               selectedContentRatings.length > 0 ||
               seriesType !== "all";
    }, [searchQuery, selectedGenres, selectedNetworks, selectedContentRatings, seriesType]);

    return {
        // Filtered data
        filteredReleases,
        
        // Available options
        availableGenres,
        availableNetworks,
        availableContentRatings,
        
        // Search
        searchQuery,
        setSearchQuery,
        
        // Filters
        selectedGenres,
        setSelectedGenres,
        selectedNetworks,
        setSelectedNetworks,
        selectedContentRatings,
        setSelectedContentRatings,
        
        // Series type
        seriesType,
        setSeriesType,
        
        // Utilities
        resetFilters,
        hasActiveFilters,
    };
};

export default useAdvancedFilters;
